import { useState } from 'react';
import { useAuth } from '@/hooks/useAuthApi';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AuthLayout from '@/components/auth/AuthLayout';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { resetPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setIsLoading(true);

    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (error: any) {
      setError(
        error.code === 'auth/user-not-found'
          ? 'No account found with this email address'
          : error.code === 'auth/invalid-email'
          ? 'Invalid email address'
          : 'An error occurred. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Input styles for dark theme - h-10 (40px height)
  const inputStyles = {
    backgroundColor: 'transparent',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    color: 'white',
    padding: '10px 16px',
    fontSize: '16px',
    width: '100%',
    outline: 'none',
    transition: 'border-color 0.2s ease',
    height: '40px',
    letterSpacing: '-0.16px',
  };

  return (
    <AuthLayout
      title="Reset your password"
      pageTitle="Reset Password"
      showTermsLinks={false}
    >
      <p className="text-center text-[#a4a4a4] text-sm -mt-2 mb-6 tracking-[-0.14px]">
        Enter your email address and we'll send you a link to reset your password
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-red-500/20 border border-red-500/50 text-red-200 p-3 rounded-xl flex items-start gap-2"
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence mode="wait">
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-green-500/20 border border-green-500/50 text-green-200 p-3 rounded-xl flex items-start gap-2"
            >
              <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span className="text-sm">Password reset email sent. Check your inbox for further instructions.</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Email field */}
        <div className="flex flex-col gap-2">
          <label htmlFor="email" className="text-sm text-[#a4a4a4] tracking-[-0.14px]">
            Email
          </label>
          <input
            id="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={inputStyles}
            className="focus:border-white/30 placeholder:text-[#b2b2b2]"
          />
        </div>

        {/* Reset password button */}
        <button
          type="submit"
          disabled={isLoading || success}
          className="w-full h-10 bg-white text-[#404040] font-medium rounded-[36px] hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-base tracking-[-0.16px] capitalize"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <svg
                className="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Sending email...
            </span>
          ) : (
            'Reset Password'
          )}
        </button>

        <div className="text-center text-sm text-[#a4a4a4] tracking-[-0.14px]">
          <Link to="/login" className="text-white underline underline-offset-4 hover:text-white/80">
            Back to login
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
};

export default ForgotPassword;
